import React, { useRef, useState, useEffect } from "react";
import { SessionTheme, getHud } from "../../config/sessionThemes";
import { VoiceControls } from "./VoiceControls";

interface HudTopBarProps {
  sessionId: string;
  isDM: boolean;
  isConnected: boolean;
  reconnectAttempts: number;
  theme: SessionTheme;
  // Voice
  voiceEnabled: boolean;
  voiceMuted: boolean;
  voiceVolume: number;
  onToggleVoice: () => void;
  onToggleMute: () => void;
  onVolumeChange: (_v: number) => void;
  onOpenAudioSettings: () => void;
}

/**
 * Transparent gradient overlay bar that sits at the very top of the game board.
 * Shows session info pills and voice controls without covering the map.
 */
export const HudTopBar: React.FC<HudTopBarProps> = ({
  sessionId,
  isDM,
  isConnected,
  reconnectAttempts,
  theme,
  voiceEnabled,
  voiceMuted,
  voiceVolume,
  onToggleVoice,
  onToggleMute,
  onVolumeChange,
  onOpenAudioSettings,
}) => {
  const hud = getHud(theme);
  const [elapsed, setElapsed] = useState("00:00:00");
  const startRef = useRef(Date.now());

  // Live session timer
  useEffect(() => {
    const id = setInterval(() => {
      const s = Math.floor((Date.now() - startRef.current) / 1000);
      const h = Math.floor(s / 3600)
        .toString()
        .padStart(2, "0");
      const m = Math.floor((s % 3600) / 60)
        .toString()
        .padStart(2, "0");
      const sc = (s % 60).toString().padStart(2, "0");
      setElapsed(`${h}:${m}:${sc}`);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const pillStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    background: `${hud.toolbarBg}`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: `1px solid ${hud.toolbarBorder}`,
    color: theme.colors.textSecondary,
    whiteSpace: "nowrap",
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 48,
        background: `linear-gradient(180deg, ${hud.topBarFrom} 0%, transparent 100%)`,
        display: "flex",
        alignItems: "center",
        padding: "0 16px",
        gap: 10,
        pointerEvents: "none",
        zIndex: 80,
      }}
    >
      {/* Everything inside has pointer-events re-enabled */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, pointerEvents: "auto" }}>
        <span style={{ fontWeight: 800, fontSize: 15, color: theme.colors.textPrimary }}>
          ⚔️ Session #{sessionId}
        </span>
        {isDM ? (
          <span
            style={{
              background: `${hud.toolbarButtonOn}`,
              border: `1px solid ${hud.snapAccent}`,
              color: theme.colors.accent,
              borderRadius: 12,
              padding: "2px 8px",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            Dungeon Master
          </span>
        ) : (
          <span
            style={{
              background: `rgba(59,130,246,.2)`,
              border: `1px solid rgba(59,130,246,.4)`,
              color: "#93c5fd",
              borderRadius: 12,
              padding: "2px 8px",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            Player
          </span>
        )}
      </div>

      {/* Status pills */}
      <div style={{ display: "flex", gap: 6, pointerEvents: "auto", flexWrap: "nowrap" }}>
        <span style={{ ...pillStyle }}>⏱ {elapsed}</span>
      </div>

      {/* Right cluster: voice + connection */}
      <div
        style={{
          marginLeft: "auto",
          display: "flex",
          gap: 8,
          alignItems: "center",
          pointerEvents: "auto",
        }}
      >
        {/* Voice controls — compact inline */}
        <VoiceControls
          isMuted={voiceMuted}
          isEnabled={voiceEnabled}
          volume={voiceVolume}
          onToggleMute={onToggleMute}
          onToggleEnabled={onToggleVoice}
          onVolumeChange={onVolumeChange}
          onOpenSettings={onOpenAudioSettings}
        />

        {/* Connection status */}
        <div data-testid="connection-status" style={pillStyle}>
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: isConnected ? "#22c55e" : "#ef4444",
              boxShadow: isConnected ? "0 0 5px #22c55e" : "0 0 5px #ef4444",
              flexShrink: 0,
            }}
          />
          <span
            className={isConnected ? "text-green-400" : "text-red-400"}
            style={{ fontSize: 10, fontWeight: 700 }}
          >
            {isConnected ? "Connected" : `Reconnecting (${reconnectAttempts})`}
          </span>
        </div>
      </div>
    </div>
  );
};
